/**
 * MicButton — web port of `apps/WunderGuide/common/MicButton.tsx`.
 *
 * Faithful 1:1 port of the signature orb. Same layered render order as RN:
 *
 *   pulses (3)         — fade+scale rings, listening/connecting
 *   mic ripples (3)    — solid pink rings emitted by `micAudioLevel > 0.03`
 *   bot ripples (3)    — dotted pink rings emitted by `botAudioLevel > 0.03`
 *   border runner      — rotating SVG arc with gradient stroke
 *   outer shadow box   — drop shadow
 *   glass rim          — linear gradient (white → blue → pink → white)
 *   container          — backdrop-blur glass face, inner-shadow + bottom highlight
 *   icons              — mic / connecting-sparkle / listening-sparkle / close-X
 *   sparkle emitter    — 24 particles for `isBotWorking`
 *
 * Listening drives heartbeat (1 → 1.12 / 900 ms), idle drives entice
 * pop+jiggle every 5–9 s. Press scales to 0.94. All animation parameters
 * mirror RN's MicButton (durations, easings, offsets, colors).
 */
import React, { useEffect, useMemo, useRef } from 'react';
import WgSparkle from './WgSparkle';
import './MicButton.css';

interface MicButtonProps {
  isListening: boolean;
  isConnecting?: boolean;
  hasRemoteParticipant?: boolean;
  onPress: () => void;
  micAudioLevel?: number;
  botAudioLevel?: number;
  isBotWorking?: boolean;
  /** Wrapper size in px (RN default 200; container/icons scale from this). */
  wrapperSize?: number;
}

const MIC_COLOR = '#ff2cb9';
const NUM_RIPPLES = 3;
const RIPPLE_DURATION_MS = 1000;
const RIPPLE_INTERVAL_MS = 250;
const SPARKLE_COUNT = 24;
const SPARKLE_DURATION_MS = 1900;
const SPARKLE_TAIL_MS = 1100;

const MicButton: React.FC<MicButtonProps> = ({
  isListening,
  isConnecting = false,
  hasRemoteParticipant = false,
  onPress,
  micAudioLevel = 0,
  botAudioLevel = 0,
  isBotWorking = false,
  wrapperSize = 200,
}) => {
  const isIdle = !isListening && !isConnecting;
  const isReadyToEnd = isListening && hasRemoteParticipant && !isConnecting;

  // Scale the geometry (RN uses dims object with proportional sizes).
  const dims = useMemo(() => {
    const s = wrapperSize / 200;
    return {
      wrapper: wrapperSize,
      container: Math.round(88 * s),
      glassRim: Math.round(104 * s),
      outerShadow: Math.round(116 * s),
      pulse: Math.round(120 * s),
      ripple: Math.round(140 * s),
      borderRunner: Math.round(104 * s),
      sparkleSize: Math.round(72 * s),
      iconSize: Math.round(36 * s),
      emitterDistMin: Math.round(30 * s),
      emitterDistMax: Math.round(90 * s),
    };
  }, [wrapperSize]);

  const containerRef = useRef<HTMLDivElement | null>(null);
  const ripplesLayerRef = useRef<HTMLDivElement | null>(null);
  const sparklesLayerRef = useRef<HTMLDivElement | null>(null);

  /* ── Mic-level ripple emitter ───────────────────────────────────────── */
  useEffect(() => {
    const layer = ripplesLayerRef.current;
    if (!layer || micAudioLevel <= 0.03) return;
    const id = window.setInterval(() => {
      spawnRipple(
        layer,
        dims.ripple,
        MIC_COLOR,
        /* dotted */ false,
        RIPPLE_DURATION_MS,
      );
    }, RIPPLE_INTERVAL_MS);
    spawnRipple(layer, dims.ripple, MIC_COLOR, false, RIPPLE_DURATION_MS);
    return () => window.clearInterval(id);
  }, [micAudioLevel > 0.03, dims.ripple]); // eslint-disable-line react-hooks/exhaustive-deps

  /* ── Bot-level ripple emitter (dotted) ──────────────────────────────── */
  useEffect(() => {
    const layer = ripplesLayerRef.current;
    if (!layer || botAudioLevel <= 0.03) return;
    const id = window.setInterval(() => {
      spawnRipple(
        layer,
        dims.ripple,
        MIC_COLOR,
        /* dotted */ true,
        RIPPLE_DURATION_MS,
      );
    }, RIPPLE_INTERVAL_MS);
    return () => window.clearInterval(id);
  }, [botAudioLevel > 0.03, dims.ripple]); // eslint-disable-line react-hooks/exhaustive-deps

  /* ── Bot-working sparkle emitter (24 particles, burst on start, tail) ─ */
  const tailUntilRef = useRef<number>(0);
  const prevWorkingRef = useRef<boolean>(false);
  useEffect(() => {
    const layer = sparklesLayerRef.current;
    if (!layer) return;
    const wasWorking = prevWorkingRef.current;
    if (isBotWorking && !wasWorking) {
      spawnSparkle(layer, dims, SPARKLE_DURATION_MS);
      window.setTimeout(
        () => spawnSparkle(layer, dims, SPARKLE_DURATION_MS),
        60,
      );
      window.setTimeout(
        () => spawnSparkle(layer, dims, SPARKLE_DURATION_MS),
        120,
      );
      tailUntilRef.current = Date.now() + SPARKLE_TAIL_MS;
    } else if (!isBotWorking && wasWorking) {
      tailUntilRef.current = Date.now() + SPARKLE_TAIL_MS;
    }
    prevWorkingRef.current = isBotWorking;
  }, [isBotWorking, dims]);

  useEffect(() => {
    const layer = sparklesLayerRef.current;
    if (!layer) return;
    const intervalMs = isBotWorking ? 160 : 240;
    const tick = () => {
      spawnSparkle(layer, dims, SPARKLE_DURATION_MS);
      if (!isBotWorking && Date.now() >= tailUntilRef.current) {
        window.clearInterval(id);
      }
    };
    const shouldEmit = isBotWorking || Date.now() < tailUntilRef.current;
    if (!shouldEmit) return;
    const id = window.setInterval(tick, intervalMs);
    tick();
    return () => window.clearInterval(id);
  }, [isBotWorking, dims]);

  /* ── Accessibility label (matches RN) ───────────────────────────────── */
  const aria = isReadyToEnd
    ? 'Tap to end call'
    : isConnecting
      ? 'Connecting to assistant'
      : isListening
        ? 'Listening to assistant'
        : 'Tap to start call';

  /* ── State class flags drive CSS animations ─────────────────────────── */
  const stateClass = [
    isIdle && 'wg-mic--idle',
    isListening && 'wg-mic--listening',
    isConnecting && 'wg-mic--connecting',
    isReadyToEnd && 'wg-mic--end',
    isBotWorking && 'wg-mic--working',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div
      ref={containerRef}
      className={`wg-mic ${stateClass}`}
      style={{
        ['--wg-mic-wrapper' as any]: `${dims.wrapper}px`,
        ['--wg-mic-container' as any]: `${dims.container}px`,
        ['--wg-mic-glass-rim' as any]: `${dims.glassRim}px`,
        ['--wg-mic-shadow' as any]: `${dims.outerShadow}px`,
        ['--wg-mic-pulse' as any]: `${dims.pulse}px`,
        ['--wg-mic-runner' as any]: `${dims.borderRunner}px`,
        width: dims.wrapper,
        height: dims.wrapper,
      }}
    >
      {/* Pulses (3) */}
      <span className="wg-mic__pulse wg-mic__pulse--1" aria-hidden />
      <span className="wg-mic__pulse wg-mic__pulse--2" aria-hidden />
      <span className="wg-mic__pulse wg-mic__pulse--3" aria-hidden />

      {/* Ripple emitter layer (mic + bot ripples spawn here) */}
      <div ref={ripplesLayerRef} className="wg-mic__ripples" aria-hidden />

      {/* Border runner (rotating gradient arc) */}
      <div className="wg-mic__runner" aria-hidden>
        <svg
          width={dims.borderRunner}
          height={dims.borderRunner}
          viewBox={`0 0 ${dims.borderRunner} ${dims.borderRunner}`}
        >
          <defs>
            <linearGradient
              id="wgMicRunnerGrad"
              x1="0%"
              y1="0%"
              x2="100%"
              y2="0%"
            >
              <stop offset="0%" stopColor="white" stopOpacity="0.8" />
              <stop offset="30%" stopColor="white" stopOpacity="0.5" />
              <stop offset="60%" stopColor="white" stopOpacity="0.2" />
              <stop offset="100%" stopColor="white" stopOpacity="0" />
            </linearGradient>
          </defs>
          <circle
            cx={dims.borderRunner / 2}
            cy={dims.borderRunner / 2}
            r={dims.borderRunner / 2 - 2}
            stroke="url(#wgMicRunnerGrad)"
            strokeWidth={2}
            fill="none"
            strokeDasharray={`${Math.round(Math.PI * (dims.borderRunner / 2 - 2))} ${Math.round(Math.PI * (dims.borderRunner / 2 - 2))}`}
          />
        </svg>
      </div>

      {/* Outer drop-shadow wrapper */}
      <div className="wg-mic__outer-shadow" aria-hidden>
        {/* Glass rim — linear gradient white→blue→pink→white */}
        <div className="wg-mic__glass-rim">
          {/* Inner button face (interactive). */}
          <button
            type="button"
            className="wg-mic__face"
            onClick={onPress}
            aria-pressed={isListening}
            aria-label={aria}
          >
            {/* Inner gradient overlays for depth */}
            <span className="wg-mic__face-inner-shadow" aria-hidden />
            <span className="wg-mic__face-bottom-highlight" aria-hidden />

            {/* Idle: mic icon + 3 entice sparkles */}
            <span className="wg-mic__icon wg-mic__icon--mic">
              <MicGlyph size={dims.iconSize} />
            </span>

            {/* Connecting: spinning sparkle */}
            <span className="wg-mic__icon wg-mic__icon--connecting">
              <WgSparkle size={dims.sparkleSize} />
            </span>

            {/* Listening: twinkling sparkle */}
            <span className="wg-mic__icon wg-mic__icon--listening">
              <WgSparkle size={dims.sparkleSize} />
            </span>

            {/* End: close X */}
            <span className="wg-mic__icon wg-mic__icon--end">
              <CloseGlyph size={dims.iconSize} />
            </span>

            {/* Bot-working sparkle emitter layer */}
            <div
              ref={sparklesLayerRef}
              className="wg-mic__sparkles"
              aria-hidden
            />
          </button>
        </div>
      </div>
    </div>
  );
};

/* ──────────────────────────────────────────────────────────────────────── */
/* Imperative ripple / sparkle spawners — Web Animations API.              */
/* ──────────────────────────────────────────────────────────────────────── */

function spawnRipple(
  layer: HTMLElement,
  size: number,
  color: string,
  dotted: boolean,
  durationMs: number,
) {
  const el = document.createElement('span');
  el.className = 'wg-mic__ripple';
  el.style.width = `${size}px`;
  el.style.height = `${size}px`;
  el.style.borderRadius = `${size / 2}px`;
  el.style.borderColor = color;
  if (dotted) {
    el.style.borderStyle = 'dotted';
    el.style.borderWidth = '3px';
  }
  layer.appendChild(el);
  const anim = el.animate(
    [
      { transform: 'translate(-50%, -50%) scale(0.7)', opacity: 0.7 },
      { transform: 'translate(-50%, -50%) scale(2.2)', opacity: 0 },
    ],
    { duration: durationMs, easing: 'cubic-bezier(0.22, 1, 0.36, 1)' },
  );
  anim.onfinish = () => el.remove();
  anim.oncancel = () => el.remove();
}

function spawnSparkle(
  layer: HTMLElement,
  dims: { emitterDistMin: number; emitterDistMax: number },
  durationMs: number,
) {
  const angle = Math.random() * Math.PI * 2;
  const distance =
    dims.emitterDistMin +
    Math.random() * (dims.emitterDistMax - dims.emitterDistMin);
  const tx = Math.cos(angle) * distance;
  const ty = Math.sin(angle) * distance;
  const scale = 0.7 + Math.random() * 0.9;
  const rotate = -20 + Math.random() * 40;
  const sizePx = 14 + Math.floor(Math.random() * 6) * 2;

  const el = document.createElement('span');
  el.className = 'wg-mic__sparkle-particle';
  el.style.width = `${sizePx}px`;
  el.style.height = `${sizePx}px`;
  el.innerHTML = `
    <svg viewBox="0 0 1024 1024" width="${sizePx}" height="${sizePx}" aria-hidden>
      <path fill="#fff" d="M509.4,336.2c-10.1,45.2-13.7,102.5-50.7,134.8-33.6,29.3-83,28.3-124.2,38.2,40.2,7.9,100.6,10.1,131,40.8,31.4,31.7,34.3,98.5,44.5,139.9,13.1-44.5,11.5-97.9,43.5-134.7,31.6-36.4,87.2-38.4,131.5-44.5,1.6-.2,3.2-.1,3.6-2.1-52.8-11.3-116.6-12.3-147.1-64-18.5-31.3-22.8-74.8-30-109.9l-2.2,1.5Z" />
    </svg>`;
  layer.appendChild(el);
  const anim = el.animate(
    [
      {
        transform: `translate(-50%, -50%) translate(0px, 0px) scale(${scale}) rotate(${rotate}deg)`,
        opacity: 0,
      },
      {
        transform: `translate(-50%, -50%) translate(${tx * 0.1}px, ${ty * 0.1}px) scale(${scale}) rotate(${rotate}deg)`,
        opacity: 0.9,
        offset: 140 / durationMs,
      },
      {
        transform: `translate(-50%, -50%) translate(${tx}px, ${ty}px) scale(${scale}) rotate(${rotate}deg)`,
        opacity: 0,
      },
    ],
    { duration: durationMs, easing: 'cubic-bezier(0.33, 1, 0.68, 1)' },
  );
  anim.onfinish = () => el.remove();
  anim.oncancel = () => el.remove();
}

/* ── Inline glyphs (mic, close) ─────────────────────────────────────────  */

function MicGlyph({ size }: { size: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="#fff"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <rect x="9" y="3" width="6" height="12" rx="3" fill="#fff" />
      <path d="M5 11a7 7 0 0 0 14 0" />
      <line x1="12" y1="18" x2="12" y2="22" />
      <line x1="9" y1="22" x2="15" y2="22" />
    </svg>
  );
}

function CloseGlyph({ size }: { size: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="#fff"
      strokeWidth="2.2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <line x1="6" y1="6" x2="18" y2="18" />
      <line x1="18" y1="6" x2="6" y2="18" />
    </svg>
  );
}

export default MicButton;
